import { useState, useEffect } from "react";
import styles from "./../../styles/charts/chartStyle.module.css";
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Paper,
  Modal,
  Box,
  TextField,
  Autocomplete,
  Typography,
} from "@mui/material";
import LineChart from "./protoCharts/LineChart";
import {
  useAllChallengeNames,
  useAllFundNames,
  useCompletedCounts,
} from "../../lib/hooks";
import {
  getChartBottomIcons,
  getChartInfoIcons,
} from "./twitter/TwitterWordCloud";
import { completedCountFormDataType } from "../../lib/analyticsAPI";
import LoadingError from "../common/LoadingError";
import { desc } from "../../lib/chartDescription";

const CompletedCountsLineChart = (props: any) => {
  const [fund_id, setFund_id] = useState<any[]>([]);
  const [challenge_id, setChallenge_id] = useState<any[]>([]);
  const [xAccumulateBy, setXAccumulatedBy] = useState<string>("QuarterName");
  const [yAccumulateQ, setYAccumulateQ] = useState<string>("True");

  const [completedCountsInfo, setCompletedCountsInfo] =
    useState<completedCountFormDataType>({
      fund_id: "All",
      challenge_id: "All",
      xAccumulateBy: "QuarterName",
      yAccumulateQ: "True",
    });

  useEffect(() => {
    setCompletedCountsInfo((prevState: any) => ({
      ...prevState,
      fund_id: fund_id.length === 0 ? "All" : fund_id.map((item) => item.id),
      challenge_id:
        challenge_id.length === 0 ? "All" : challenge_id.map((item) => item.id),
      xAccumulateBy: xAccumulateBy,
      yAccumulateQ: yAccumulateQ,
    }));
  }, [fund_id, challenge_id, xAccumulateBy, yAccumulateQ]);

  const {
    allChallengeNames,
    loading: allChallengeNameLoading,
    error: allChallengeNameError,
  } = useAllChallengeNames();

  let {
    loading: fundNameLoading,
    allFundNames,
    error: fundNamError,
  } = useAllFundNames();

  let challenge_dropDownOption =
    !fundNameLoading &&
    !allChallengeNameLoading &&
    allChallengeNameError === null &&
    fundNamError === null
      ? fund_id.length > 0
        ? allChallengeNames
            .filter((item) =>
              fund_id.map((item) => item.id).includes(item.fund_id)
            )
            .map((item) => {
              return {
                label: `${item.fund_name}:${item.challenge_name}`,
                id: item.challenge_id,
              };
            })
        : allChallengeNames.map((item) => {
            return {
              label: `${item.fund_name}:${item.challenge_name}`,
              id: item.challenge_id,
            };
          })
      : [];

  useEffect(() => {
    // The following checks handle the following case
    //1. fund4 is selected and challenge from fund4 is selected. If fund is deselected , then challenge should deselect
    //2. fund4 and fund5 is selected. challenge from fund4 is selected. If fund5 is removed , then it should not clear the challenge
    // but if fund4 is remove then it should clear challenge
    if (challenge_id.length > 0) {
      let newArr = challenge_id.filter((challenge) =>
        fund_id
          .map((item) => item.id)
          .includes(
            allChallengeNames.filter(
              (allChallenge) => allChallenge.challenge_id === challenge.id
            )[0]?.fund_id
          )
      );
      setChallenge_id(newArr);
    }
    //eslint-disable-next-line
  }, [fund_id.length]);

  const {
    error: countsError,
    loading: countsLoading,
    completedCountsData,
  } = useCompletedCounts(completedCountsInfo);

  function resetFilter() {
    setCompletedCountsInfo({
      xAccumulateBy: "QuarterName",
      yAccumulateQ: "True",
      challenge_id: allChallengeNames[0].challenge_id,
    });
    setFund_id([]);
    setChallenge_id([]);
  }

  const toShortLabel = (longLabels: string[]) => {
    return longLabels.map((longLabel: string) =>
      longLabel.replace("Quarter", "Q")
    );
  };

  let labels: string[] = [];
  let series: any = [];
  let graph = (
    <LineChart
      labels={toShortLabel(labels)}
      chartData={series}
      yLabel="Counts"
      title="The Count of Completed Projects"
    />
  );

  if (!countsLoading) {
    if (completedCountsData.length > 0) {
      labels = completedCountsData[0].items.map((item: any) => {
        return item.interval;
      });

      for (let i = 0; i < completedCountsData.length; i++) {
        let year = completedCountsData[i].year;
        let data: any = completedCountsData[i].items.map((item: any) => {
          return item.count;
        });
        series.push({ name: year, data: data });
      }
    } else {
      if (countsError) {
        graph = (
          <LoadingError
            h1="No Data found for options provided."
            hideReset={false}
            customeReset={{
              head: "Please try a different input",
              resetHandler: resetFilter,
              subhead: " Reset Parameters",
            }}
          />
        );
      }
    }
  }

  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };

  const content = (showMaxButton: boolean) => {
    return (
      <Paper elevation={3} className={styles.chartContainer}>
        <div className="horizontalCenter">
          <Typography className={styles.chartHeader}>
            Number of Projects Completed
          </Typography>
        </div>

        <div className="horizontalCenter">
          <div className={styles.flexibleControlRow}>
            <FormControl className={styles.flexibleformControl} size="small">
              <Autocomplete
                multiple={true}
                id="combo-box-demo"
                options={
                  !fundNameLoading && fundNamError === null
                    ? allFundNames.map((item) => {
                        return {
                          label: item.fund_name,
                          id: item.fund_id,
                        };
                      })
                    : []
                }
                size={"small"}
                onChange={(event: any, newValue: any[] | null) => {
                  setFund_id(newValue as any[]);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                // disableClearable
                value={fund_id}
                renderInput={(params) => (
                  <TextField {...params} label="Select Funds" />
                )}
              />
            </FormControl>

            <FormControl className={styles.flexibleformControl} size="small">
              <Autocomplete
                multiple={true}
                id="combo-box-demo"
                options={challenge_dropDownOption}
                size={"small"}
                onChange={(event: any, newValue: any[] | null) => {
                  setChallenge_id(newValue as any[]);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                // disableClearable
                value={challenge_id}
                renderInput={(params) => (
                  <TextField {...params} label="Select Challenges" />
                )}
              />
            </FormControl>
          </div>
        </div>

        <div className="horizontalCenter">
          <div className={styles.flexibleControlRow}>
            <FormControl size="small" className={styles.flexibleformControl}>
              <InputLabel className={styles.flexSelectFont}>
                Accumulate By
              </InputLabel>
              <Select
                className={styles.flexSelectFont}
                value={xAccumulateBy}
                label="Accumulate By"
                onChange={(event: any) => setXAccumulatedBy(event.target.value)}
              >
                <MenuItem value="QuarterName" key="Quater">
                  Quarter
                </MenuItem>
                <MenuItem value="MonthName" key="Month">
                  Month
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl className={styles.flexibleformControl} size="small">
              <InputLabel className={styles.flexSelectFont}>
                Accumulate Counts
              </InputLabel>
              <Select
                className={styles.flexSelectFont}
                value={yAccumulateQ}
                label="Accumulate Counts"
                onChange={(event) => setYAccumulateQ(event.target.value)}
                defaultValue="True"
              >
                <MenuItem value="True" key="True">
                  True
                </MenuItem>
                <MenuItem value="False" key="False">
                  False
                </MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>

        {graph}

        {getChartBottomIcons(showMaxButton, setOpen)}
        {getChartInfoIcons(desc.project.compledCounts)}
      </Paper>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className={styles.fullScreenChart}>{content(false)}</Box>
      </Modal>
      {content(true)}
    </>
  );
};

export default CompletedCountsLineChart;
